import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ClubProvider } from "./contexts/ClubContext";
import { ClubSettingsProvider } from "./contexts/ClubSettingsContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";

// Pages
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import PricingPage from "./pages/PricingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ContactPage from "./pages/ContactPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import ClubDashboard from "./pages/ClubDashboard";
import EvaluationSetup from "./pages/EvaluationSetup";
import SessionCheckIn from "./pages/SessionCheckIn";
import PlayerManagement from "./pages/PlayerManagement";
import EvaluationResults from "./pages/EvaluationResults";
import SkillBasedResults from "./pages/SkillBasedResults";
import StaffManagement from "./pages/StaffManagement";
import ChangePassword from "./pages/ChangePassword";
import SkillsManagement from "./pages/SkillsManagement";
import GroupsManagement from "./pages/GroupsManagement";
import BillingManagement from "./pages/BillingManagement";
import MarketingManagement from "./pages/MarketingManagement";
import ClubManagement from "./pages/ClubManagement";
import Invoices from "./pages/Invoices";
import Evaluations from "./pages/Evaluations";
import Settings from "./pages/Settings";
import PublicParentReport from "./pages/PublicParentReport";

import "./App.css";

// Legacy redirect: prepends /portal to current path
const LegacyRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`/portal${location.pathname}${location.search}${location.hash}`}
      replace
    />
  );
};

// Component to handle password change requirement
const PasswordChangeGuard = ({ children }) => {
  const { requiresPasswordChange, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  if (requiresPasswordChange) {
    return <ChangePassword isForced={true} />;
  }

  return children;
};

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  // Full-page routes skip the app shell (navbar, container, background)
  const isFullPageRoute =
    location.pathname === "/" ||
    location.pathname === "/how-it-works" ||
    location.pathname === "/pricing" ||
    location.pathname === "/privacy" ||
    location.pathname === "/terms" ||
    location.pathname === "/contact" ||
    location.pathname.startsWith("/report/");
  const showAppShell = !isFullPageRoute;

  return (
    <div className={showAppShell ? "min-h-screen bg-gray-50" : ""}>
      <ClubSettingsProvider>
        {showAppShell && isAuthenticated && <Navbar />}
        <main
          className={
            showAppShell && isAuthenticated ? "container mx-auto px-4 py-8" : ""
          }
        >
          <Routes>
            {/* Full-page public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/report/:token" element={<PublicParentReport />} />

            {/* Portal routes */}
            <Route path="/portal/login" element={<Login />} />
            <Route
              path="/portal/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/dashboard"
              element={
                <ProtectedRoute
                  role={["club_admin", "coach", "super_admin", "coordinator"]}
                >
                  <PasswordChangeGuard>
                    <ClubDashboard />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/evaluation/:evalId"
              element={
                <ProtectedRoute
                  role={["club_admin", "coach", "super_admin", "coordinator"]}
                >
                  <PasswordChangeGuard>
                    <EvaluationSetup />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/evaluation/:evalId/session/:sessionId/checkin"
              element={
                <ProtectedRoute
                  role={["club_admin", "coach", "super_admin", "coordinator"]}
                >
                  <PasswordChangeGuard>
                    <SessionCheckIn />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/players"
              element={
                <ProtectedRoute role={["club_admin", "coach", "super_admin"]}>
                  <PasswordChangeGuard>
                    <PlayerManagement />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/staff"
              element={
                <ProtectedRoute role={["club_admin", "super_admin"]}>
                  <PasswordChangeGuard>
                    <StaffManagement />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/evaluation/:evalId/results"
              element={
                <ProtectedRoute role={["club_admin", "coach", "super_admin"]}>
                  <PasswordChangeGuard>
                    <EvaluationResults />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/evaluation/:evalId/skill-results"
              element={
                <ProtectedRoute role={["club_admin", "coach", "super_admin"]}>
                  <PasswordChangeGuard>
                    <SkillBasedResults />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/skills"
              element={
                <ProtectedRoute role={["club_admin", "coach", "super_admin"]}>
                  <PasswordChangeGuard>
                    <SkillsManagement />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/groups"
              element={
                <ProtectedRoute role={["club_admin", "coach", "super_admin"]}>
                  <PasswordChangeGuard>
                    <GroupsManagement />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/invoices"
              element={
                <ProtectedRoute role={["club_admin", "super_admin"]}>
                  <PasswordChangeGuard>
                    <Invoices />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/evaluations"
              element={
                <ProtectedRoute
                  role={["club_admin", "coach", "super_admin", "coordinator"]}
                >
                  <PasswordChangeGuard>
                    <Evaluations />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/club/:clubId/settings"
              element={
                <ProtectedRoute role={["club_admin", "super_admin"]}>
                  <PasswordChangeGuard>
                    <Settings />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />

            {/* Admin routes (unchanged) */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute role="super_admin">
                  <PasswordChangeGuard>
                    <SuperAdminDashboard />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/billing"
              element={
                <ProtectedRoute role="super_admin">
                  <PasswordChangeGuard>
                    <BillingManagement />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/marketing"
              element={
                <ProtectedRoute role="super_admin">
                  <PasswordChangeGuard>
                    <MarketingManagement />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clubs"
              element={
                <ProtectedRoute role="super_admin">
                  <PasswordChangeGuard>
                    <ClubManagement />
                  </PasswordChangeGuard>
                </ProtectedRoute>
              }
            />

            {/* Legacy redirects: old paths → /portal equivalents */}
            <Route path="/login" element={<LegacyRedirect />} />
            <Route path="/change-password" element={<LegacyRedirect />} />
            <Route path="/club/*" element={<LegacyRedirect />} />
            <Route path="/evaluation/*" element={<LegacyRedirect />} />
          </Routes>
        </main>
        <Toaster position="top-right" />
      </ClubSettingsProvider>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ClubProvider>
          <Router>
            <AppContent />
          </Router>
        </ClubProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
